import { guessUnpairedFills } from './unpairedCargoGuess';
import type { RouteOrderDto, TransportRouteDetailDto } from '../../dto/transport';
import type { TransportCargoItemDto, TransportSupplierOrderLoadItemDto } from '../../types/transport';
import { normalizeUnitCode } from '../../utils/units';
import type { LoadUnit } from './freightGoodsTable';
import { carriedLoadItems } from './supplierOrderFreight';
import { toEffectiveCargoItem } from './effectiveCargoQuantity';
import { formatLineDimensions } from '../../utils/productDimensions';
import { summarizeSupplierOrderRealization, type SupplierOrderRealizationLine } from './supplierOrderRealization';

/**
 * „Ładunek” of an INBOUND truck (Fabryka → klient / magazyn): one group per
 * ZD line the truck carries, with who gets it — first the customer orders (ZK)
 * whose cargo line is saved as paired with that ZD line
 * (`TransportCargoItem.supplierOrderItemId`, migration 202), then the unpaired
 * ZK cargo of the same catalog variant guessed in the truck's order, and the
 * rest „na magazyn” — then the ZK cargo no ZD line on the truck covers.
 *
 * Presentation only. Facts are the backend's: what the truck carries of each
 * ZD line (`carried`), its PZ receipt (`received`) and its freight
 * (`freightPLN`, the line's part of the saved stock share). The split of a
 * line's freight over its recipients is a quantity proportion for reading;
 * the money reaches a ZK only through the batch cost its WZ takes (FIFO).
 */
export interface LoadQuantity {
  value: number;
  unit: LoadUnit;
}

export interface LoadRecipient {
  key: string;
  kind: 'order' | 'stock';
  /** A stock part on a truck with customer orders: the ZD rest beyond them („Nadwyżka — na magazyn”, owner 2026-10-07). */
  surplus?: boolean;
  orderId: string | null;
  orderNumber: string | null;
  customerName: string | null;
  deliveryAddress: string | null;
  quantity: LoadQuantity | null;
  /** Confirmed delivery of the ZK cargo line (its own unit); null before confirmation. */
  delivered: { value: number; unit: string } | null;
  /** The line's plan when a delivered truck brought less (its own unit); the quantity above is then the delivery. */
  planned?: { value: number; unit: string } | null;
  /** Proportional part of the ZD line's freight; null when unknown or no ZD covers it. */
  freightPLN: number | null;
  /** Catalog piece weight × pieces; null when the weight or the piece count is unknown. */
  weightKg: number | null;
  /** true = from the saved ZK ↔ ZD line pair; false = guessed by variant, no ZD, or the stock part („Na magazyn” / „Nadwyżka — na magazyn”). */
  paired: boolean;
  /** Paired beyond what the truck carries of the ZD line: the excess (freight is capped at the carried part). */
  overAssigned: LoadQuantity | null;
}

export interface LoadGroup {
  key: string;
  productName: string;
  dimensions: string | null;
  /** null = ZK cargo that no ZD line on this truck covers. */
  supplierOrder: { id: string; label: string; supplierName: string | null } | null;
  /**
   * Why a group has no ZD (supplierOrder null): 'unpaired' = a ZD line of this
   * product with goods left IS on the truck, the cargo just has no pair with it;
   * 'exceeds' = all of that line is already given to others; 'noLine' = no ZD
   * line of this product on the truck. null on groups with a ZD.
   */
  missing: 'unpaired' | 'exceeds' | 'noLine' | null;
  carried: LoadQuantity | null;
  received: number | null;
  freightPLN: number | null;
  freightPerUnit: LoadQuantity | null;
  /** Freight per szt., m² and m³ — each unit the carried line has a quantity in. */
  freightPerUnits: LoadQuantity[];
  /** Whole carried line; null when the catalog has no piece weight. */
  weightKg: number | null;
  /** Catalog weight of one piece (kg); null when not entered or no ZD line. */
  pieceWeightKg: number | null;
  recipients: LoadRecipient[];
}

type Sets = Record<LoadUnit, number>;

const UNITS: LoadUnit[] = ['pcs', 'm2', 'm3'];
const EPS = 0.0005;
const round3 = (value: number) => Math.round(value * 1000) / 1000;
const round2 = (value: number) => Math.round(value * 100) / 100;

function setsOf(item: { quantityPcs?: number | null; quantityM2?: number | null; quantityM3?: number | null }): Sets {
  return { pcs: Number(item.quantityPcs || 0), m2: Number(item.quantityM2 || 0), m3: Number(item.quantityM3 || 0) };
}

/** The unit both sides know, preferring the ZD line's own unit. */
function commonUnit(preferred: LoadUnit | null, a: Sets, b: Sets): LoadUnit | null {
  const order = preferred ? [preferred, ...UNITS.filter((unit) => unit !== preferred)] : UNITS;
  return order.find((unit) => a[unit] > EPS && b[unit] > EPS) ?? null;
}

interface OpenLine {
  group: LoadGroup;
  item: TransportSupplierOrderLoadItemDto;
  unit: LoadUnit | null;
  carried: Sets;
  remaining: Sets;
}

export function buildTransportLoadTable(
  route: Pick<TransportRouteDetailDto, 'supplierOrders' | 'cargoItems' | 'routeOrders'> & { status?: string | null },
): LoadGroup[] {
  const realizationById = new Map<string, SupplierOrderRealizationLine>();
  for (const row of summarizeSupplierOrderRealization(route.supplierOrders)) {
    for (const line of row.lines) realizationById.set(line.id, line);
  }

  const open: OpenLine[] = [];
  (route.supplierOrders || []).forEach((supplierOrder) => {
    const label = supplierOrder.displayOrderNumber || supplierOrder.orderNumber;
    for (const item of carriedLoadItems(supplierOrder.loadItems)) {
      const line = realizationById.get(item.id);
      const unit = line?.unit ?? null;
      const carried = setsOf(item);
      const freightPLN = item.freightPLN ?? null;
      const weightKg = item.pieceWeightKg != null && carried.pcs > EPS ? round2(item.pieceWeightKg * carried.pcs) : null;
      const group: LoadGroup = {
        key: `${supplierOrder.id}:${item.id}`,
        productName: item.name,
        dimensions: line?.dimensions || null,
        supplierOrder: { id: supplierOrder.id, label, supplierName: supplierOrder.supplierName ?? null },
        missing: null,
        carried: unit ? { value: carried[unit], unit } : null,
        received: line?.received ?? null,
        freightPLN,
        freightPerUnit: unit && freightPLN !== null && carried[unit] > EPS
          ? { value: round2(freightPLN / carried[unit]), unit }
          : null,
        freightPerUnits: freightPLN === null ? [] : UNITS
          .filter((perUnit) => carried[perUnit] > EPS)
          .map((perUnit) => ({ value: round2(freightPLN / carried[perUnit]), unit: perUnit })),
        weightKg,
        pieceWeightKg: item.pieceWeightKg ?? null,
        recipients: [],
      };
      open.push({ group, item, unit, carried, remaining: { ...carried } });
    }
  });

  const pieceWeightByVariant = new Map<string, number>();
  for (const line of open) {
    if (line.item.productVariantId && line.item.pieceWeightKg != null) pieceWeightByVariant.set(line.item.productVariantId, line.item.pieceWeightKg);
  }
  const ordersById = new Map<string, RouteOrderDto>(route.routeOrders.map((row) => [row.orderId, row]));
  const orderRank = new Map(route.routeOrders.map((row, index) => [row.orderId, index]));
  // A delivered truck carries what it delivered (plan 2026-10-13): a line
  // delivered 0 takes no ZD, weight or freight; findShortDeliveries lists it.
  const cargo = route.cargoItems
    .map((item) => toEffectiveCargoItem(item, route.status))
    .sort((a, b) => (orderRank.get(a.orderId) ?? 0) - (orderRank.get(b.orderId) ?? 0));
  const unmatched: LoadGroup[] = [];

  const recipientOf = (
    cargoItem: TransportCargoItemDto & { plannedQuantity?: number },
    part: number | string,
    quantity: LoadQuantity | null,
    freightPLN: number | null,
    weightKg: number | null,
    paired: boolean,
    overAssigned: LoadQuantity | null = null,
  ): LoadRecipient => {
    const routeOrder = ordersById.get(cargoItem.orderId);
    return {
      key: `${cargoItem.id}:${part}`,
      kind: 'order',
      orderId: cargoItem.orderId,
      orderNumber: routeOrder ? routeOrder.order.displayOrderNumber || routeOrder.order.orderNumber : null,
      customerName: routeOrder?.order.customerName || null,
      deliveryAddress: routeOrder?.deliveryAddress || null,
      quantity,
      delivered: cargoItem.deliveredQuantity == null ? null : { value: Number(cargoItem.deliveredQuantity), unit: cargoItem.unit || '' },
      planned: cargoItem.plannedQuantity != null && cargoItem.plannedQuantity !== Number(cargoItem.quantity)
        ? { value: cargoItem.plannedQuantity, unit: cargoItem.unit || '' }
        : null,
      freightPLN,
      weightKg,
      paired,
      overAssigned,
    };
  };

  // The saved pair wins: a ZK cargo line pointing at a ZD line this truck
  // carries takes its whole quantity from that line (in their common unit).
  // A pair to a line not on this truck, or with no common unit, is guessed below.
  const openByItemId = new Map(open.map((line) => [line.item.id, line]));
  const pairedCargo = new Set<string>();
  for (const cargoItem of cargo) {
    const line = cargoItem.supplierOrderItemId ? openByItemId.get(cargoItem.supplierOrderItemId) : undefined;
    if (!line) continue;
    const need = setsOf(cargoItem);
    const unit = commonUnit(line.unit, need, line.carried);
    if (!unit) continue;
    // Freight never exceeds what the truck carries of the line (review
    // 2026-10-06): the part paired beyond it is shown as an excess.
    const take = Math.min(need[unit], line.remaining[unit]);
    const ratio = take / line.carried[unit];
    const excess = need[unit] - take;
    for (const other of UNITS) line.remaining[other] = Math.max(0, line.remaining[other] - ratio * line.carried[other]);
    line.group.recipients.push(recipientOf(
      cargoItem,
      'paired',
      { value: round3(need[unit]), unit },
      line.group.freightPLN === null ? null : round2(line.group.freightPLN * ratio),
      line.group.weightKg === null ? null : round2(line.group.weightKg * ratio),
      true,
      excess > EPS ? { value: round3(excess), unit } : null,
    ));
    pairedCargo.add(cargoItem.id);
  }
  const guessed = cargo.filter((cargoItem) => !pairedCargo.has(cargoItem.id));

  // Why guessed cargo stays without a ZD (review 2026-10-06): cargo with no
  // saved pair while a ZD line of its product rides the truck = „bez pary”
  // (pick „z pozycji ZD”), whatever that line has left; a pair whose line was
  // emptied by other recipients = more than the truck carries; else no line.
  const missingReason = (cargoItem: { productVariantId?: string | null; supplierOrderItemId?: string | null }): LoadGroup['missing'] => {
    const variantId = cargoItem.productVariantId;
    if (!variantId || !open.some((line) => line.item.productVariantId === variantId)) return 'noLine';
    return cargoItem.supplierOrderItemId ? 'exceeds' : 'unpaired';
  };

  // Cargo without a pair: the shared „do przypisania” guess (unpairedCargoGuess,
  // also the form's default ZD quantities) — an exact line first, then the rest.
  const guessKey = (cargoItem: TransportCargoItemDto) => cargoItem.orderProductId || cargoItem.id;
  const lineById = new Map(open.map((line) => [line.item.id, line]));
  const { fills, left } = guessUnpairedFills(
    guessed.map((cargoItem) => ({ key: guessKey(cargoItem), variantId: cargoItem.productVariantId, need: setsOf(cargoItem) })),
    open.map((line) => ({ id: line.item.id, variantId: line.item.productVariantId, unit: line.unit, remaining: line.remaining })),
  );
  for (const cargoItem of guessed) {
    const recipient = (quantity: LoadQuantity | null, freightPLN: number | null, part: number, weightKg: number | null) => (
      recipientOf(cargoItem, part, quantity, freightPLN, weightKg, false)
    );
    let part = 0;
    for (const fill of fills.get(guessKey(cargoItem)) || []) {
      const line = lineById.get(fill.lineId);
      if (!line) continue;
      const ratio = fill.taken[fill.unit] / line.carried[fill.unit];
      for (const other of UNITS) line.remaining[other] = Math.max(0, line.remaining[other] - fill.taken[other]);
      line.group.recipients.push(recipient(
        { value: round3(fill.amount), unit: fill.unit },
        line.group.freightPLN === null ? null : round2(line.group.freightPLN * ratio),
        part++,
        line.group.weightKg === null ? null : round2(line.group.weightKg * ratio),
      ));
    }
    const need = left.get(guessKey(cargoItem)) || setsOf(cargoItem);
    if (UNITS.some((other) => need[other] > EPS)) {
      const cargoUnit = normalizeUnitCode(cargoItem.unit || '') as LoadUnit;
      const unit = UNITS.includes(cargoUnit) && need[cargoUnit] > EPS ? cargoUnit : UNITS.find((other) => need[other] > EPS) ?? null;
      unmatched.push({
        key: `unmatched:${cargoItem.id}`,
        productName: cargoItem.productName,
        dimensions: formatLineDimensions(cargoItem),
        supplierOrder: null,
        missing: missingReason(cargoItem),
        carried: null,
        received: null,
        freightPLN: null,
        freightPerUnit: null,
        freightPerUnits: [],
        weightKg: null,
        pieceWeightKg: null,
        recipients: [recipient(
          unit ? { value: round3(need[unit]), unit } : null,
          null,
          part,
          // The ZK line's own piece weight, else the catalog weight of the variant.
          need.pcs > EPS && (cargoItem.grossWeight ?? pieceWeightByVariant.get(cargoItem.productVariantId || '')) != null
            ? round2(Number(cargoItem.grossWeight ?? pieceWeightByVariant.get(cargoItem.productVariantId || '')) * need.pcs)
            : null,
        )],
      });
    }
  }

  // What no ZK takes stays in the warehouse.
  for (const line of open) {
    const unit = line.unit;
    if (!unit || line.remaining[unit] <= EPS) continue;
    const ratio = line.remaining[unit] / line.carried[unit];
    line.group.recipients.push({
      key: `${line.group.key}:stock`,
      kind: 'stock',
      surplus: (route.routeOrders || []).length > 0,
      orderId: null,
      orderNumber: null,
      customerName: null,
      deliveryAddress: null,
      quantity: { value: round3(line.remaining[unit]), unit },
      delivered: null,
      freightPLN: line.group.freightPLN === null ? null : round2(line.group.freightPLN * ratio),
      weightKg: line.group.weightKg === null ? null : round2(line.group.weightKg * ratio),
      paired: false,
      overAssigned: null,
    });
  }

  return [...open.map((line) => line.group), ...unmatched];
}

export interface LoadSupplierOrderSummary {
  id: string;
  label: string;
  supplierName: string | null;
  /** Sum of its carried lines' freight; null when any is unknown. */
  freightPLN: number | null;
}

/** The truck's ZD in order with their freight — the „Ładunek” table headers. */
export function summarizeLoadSupplierOrders(groups: LoadGroup[]): LoadSupplierOrderSummary[] {
  const byId = new Map<string, LoadSupplierOrderSummary>();
  for (const group of groups) {
    if (!group.supplierOrder) continue;
    const current = byId.get(group.supplierOrder.id) ?? {
      id: group.supplierOrder.id,
      label: group.supplierOrder.label,
      supplierName: group.supplierOrder.supplierName,
      freightPLN: 0,
    };
    current.freightPLN = current.freightPLN === null || group.freightPLN === null ? null : round2(current.freightPLN + group.freightPLN);
    byId.set(group.supplierOrder.id, current);
  }
  return Array.from(byId.values());
}
